import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ recipient_ids: [], subject: "", body: "" });

  const load = async () => {
    const { data } = await api.get("/messages"); setRows(data);
    try { const c = await api.get("/messages-contacts"); setContacts(c.data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    try { await api.post("/messages", form); toast.success(t("send")); setOpen(false); setForm({ recipient_ids: [], subject: "", body: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("messages")}</h1>
        <Button data-testid="msg-new-btn" onClick={() => setOpen(true)} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("send")}</Button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 ? <Card className="card-float p-10 text-center text-slate-400 border-slate-200">{t("noData")}</Card> :
          rows.map((m) => (
            <Card key={m.id} data-testid="row-messages" className="card-float p-4 border-slate-200 flex gap-3">
              <div className="h-10 w-10 rounded-full bg-sky-50 text-primary flex items-center justify-center shrink-0"><Mail className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800">{m.subject || "(sans objet)"}</div>
                  <div className="text-xs text-slate-400">{String(m.created_at).slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="text-xs text-slate-500 mb-1">{m.sender_name}</div>
                <div className="text-sm text-slate-600 whitespace-pre-wrap">{m.body}</div>
              </div>
            </Card>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{t("messages")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <label className="text-sm font-medium text-slate-600">{t("recipients")}</label>
              <Select value={form.recipient_ids[0] || ""} onValueChange={(v) => setForm({ ...form, recipient_ids: [v] })}>
                <SelectTrigger data-testid="msg-recipient"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("subjectField")}</label><Input data-testid="msg-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("body")}</label><Textarea data-testid="msg-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="msg-send-btn" onClick={send}>{t("send")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
